import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildGroupedTaskList,
  getTaskDisplayParts,
} from "../ProjectEconomy/expectedCostUtils";

export default function GroupedTaskPicker({
  bundle,
  value = "",
  onChange,
  disabled = false,
  placeholder = "Select task…",
  className = "",
  id,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const groups = useMemo(() => buildGroupedTaskList(bundle), [bundle]);
  const display = useMemo(() => getTaskDisplayParts(value, bundle), [value, bundle]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((group) => {
        const parentMatches = group.title.toLowerCase().includes(q);
        const matchingSubtasks = group.subtasks.filter((sub) =>
          sub.title.toLowerCase().includes(q)
        );
        if (parentMatches) return group;
        if (matchingSubtasks.length > 0) {
          return { ...group, subtasks: matchingSubtasks };
        }
        return null;
      })
      .filter(Boolean);
  }, [groups, search]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const handleSelect = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
    setSearch("");
  };

  const triggerBorder = !value
    ? "border-amber-400 ring-1 ring-amber-200"
    : "border-gray-300";

  return (
    <div className={`relative inline-block min-w-[160px] max-w-[280px] ${className}`} ref={containerRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full rounded border bg-white px-2 py-1.5 text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${triggerBorder}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1">
          {!value ? (
            <span className="text-sm text-amber-800">{placeholder}</span>
          ) : display.isSubtask ? (
            <>
              <span className="block text-xs text-gray-500 truncate">{display.parent}</span>
              <span className="block text-sm font-medium text-gray-900 truncate">{display.child}</span>
            </>
          ) : (
            <span className="block text-sm font-medium text-gray-900 truncate">{display.parent}</span>
          )}
        </span>
        <span className="text-gray-400 flex-shrink-0 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1 w-full min-w-[220px] max-w-[320px] rounded border border-gray-200 bg-white shadow-lg"
          role="listbox"
        >
          <div className="border-b border-gray-100 p-2">
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks…"
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filteredGroups.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">No tasks found</p>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.taskId}>
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {group.title}
                  </div>
                  {group.subtasks.length > 0 ? (
                    group.subtasks.map((sub) => (
                      <button
                        key={sub.value}
                        type="button"
                        role="option"
                        aria-selected={value === sub.value}
                        onClick={() => handleSelect(sub.value)}
                        className={`w-full text-left pl-6 pr-3 py-1.5 text-sm hover:bg-gray-50 ${
                          value === sub.value ? "bg-blue-50 text-blue-900 font-medium" : "text-gray-800"
                        }`}
                      >
                        {sub.title}
                        {value === sub.value && <span className="ml-1 text-blue-600">✓</span>}
                      </button>
                    ))
                  ) : (
                    <button
                      type="button"
                      role="option"
                      aria-selected={value === group.value}
                      onClick={() => handleSelect(group.value)}
                      className={`w-full text-left px-3 py-1.5 text-sm font-medium hover:bg-gray-50 ${
                        value === group.value ? "bg-blue-50 text-blue-900" : "text-gray-800"
                      }`}
                    >
                      {group.title}
                      {value === group.value && <span className="ml-1 text-blue-600">✓</span>}
                    </button>
                  )}
                  {group.subtasks.length > 0 && (
                    <button
                      type="button"
                      role="option"
                      aria-selected={value === group.value}
                      onClick={() => handleSelect(group.value)}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 border-t border-gray-50 ${
                        value === group.value ? "bg-blue-50 text-blue-900 font-medium" : "text-gray-600"
                      }`}
                    >
                      <span className="text-xs text-gray-500">Use task: </span>
                      {group.title}
                      {value === group.value && <span className="ml-1 text-blue-600">✓</span>}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
